import React, { useMemo, useState } from "react"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import Switch from "@mui/material/Switch"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import {
  addModal,
  editModal,
  getModelContentByModalId,
  addModalContent,
  editModalContent,
  deleteModalContent,
  searchModel,
} from "services/modal"
import { MEDIA_TYPE_INDEX } from "constants/global"
import { useForm } from "react-hook-form"
import { useSnackbar } from "notistack"
import { StyledModalDialog } from "./Modal.style"
import { verifyResponse } from "utils/httpRequest"
import { buildDashboardModalUrl } from "utils/platformUrl"
import MediaManager from "components/common/MediaManager"
import MediaContentDisplay from "components/common/MediaManager/MediaContentDisplay"

const CONTENT_TYPE = {
  VIDEO: 2,
  IMAGE: 3,
  TEXT_TITLE: 4,
  TEXT_DESCRIPTION: 5,
  BUTTON_MAIN: 6,
  BUTTON_OTHER: 7,
}

const createCampaignBuilderState = () => ({
  headline: "",
  subHeadline: "",
  graphicMode: "none",
  graphicUrl: "",
  graphicResource: null,
  showVideoControls: true,
  ctaText: "",
  ctaDestination: "",
  ctaDisplayTimeSeconds: "0",
  ctaOpenInNewTab: false,
})

const parseJsonObject = (value) => {
  if (!value || typeof value !== "string") return {}
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === "object" ? parsed : {}
  } catch {
    return {}
  }
}

const parseBoolean = (value, fallbackValue = false) => {
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value !== 0
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    if (normalized === "true" || normalized === "1") return true
    if (normalized === "false" || normalized === "0") return false
  }
  return fallbackValue
}

const parseNonNegativeNumber = (value) => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed < 0) return 0
  return Math.floor(parsed)
}

const isExternalVideoUrl = (value) =>
  /^(https?:\/\/)(?:www\.)?(?:player\.)?vimeo\.com\/.+/i.test(
    String(value || "").trim()
  ) ||
  /^(https?:\/\/)(?:www\.)?(?:youtube\.com|youtu\.be)\/.+/i.test(
    String(value || "").trim()
  )

const isExternalImageUrl = (value) =>
  /^(https?:\/\/).+/i.test(String(value || "").trim())

const getVideoEmbedUrl = (value) => {
  const raw = String(value || "").trim()
  if (!raw) return ""

  const directVimeoMatch = raw.match(
    /^https?:\/\/(?:www\.)?player\.vimeo\.com\/video\/(\d+)/i
  )
  if (directVimeoMatch?.[1]) {
    return `https://player.vimeo.com/video/${directVimeoMatch[1]}`
  }
  const vimeoMatch = raw.match(/^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/i)
  if (vimeoMatch?.[1]) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`
  }

  const youtubeEmbedMatch = raw.match(
    /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/i
  )
  if (youtubeEmbedMatch?.[1]) {
    return `https://www.youtube.com/embed/${youtubeEmbedMatch[1]}`
  }
  const youtubeShortMatch = raw.match(
    /^https?:\/\/(?:www\.)?youtu\.be\/([A-Za-z0-9_-]{6,})/i
  )
  if (youtubeShortMatch?.[1]) {
    return `https://www.youtube.com/embed/${youtubeShortMatch[1]}`
  }
  const youtubeWatchMatch = raw.match(
    /^https?:\/\/(?:www\.)?youtube\.com\/watch\?[^#]*v=([A-Za-z0-9_-]{6,})/i
  )
  if (youtubeWatchMatch?.[1]) {
    return `https://www.youtube.com/embed/${youtubeWatchMatch[1]}`
  }
  const youtubeShortsMatch = raw.match(
    /^https?:\/\/(?:www\.)?youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/i
  )
  if (youtubeShortsMatch?.[1]) {
    return `https://www.youtube.com/embed/${youtubeShortsMatch[1]}`
  }
  return raw
}

const getFirstContentByType = (contents, type) =>
  (contents || []).find((item) => Number(item?.type) === Number(type))

const parseCampaignBuilderState = (contents) => {
  const videoItem = getFirstContentByType(contents, CONTENT_TYPE.VIDEO)
  const imageItem = getFirstContentByType(contents, CONTENT_TYPE.IMAGE)
  const titleItem = getFirstContentByType(contents, CONTENT_TYPE.TEXT_TITLE)
  const descriptionItem = getFirstContentByType(
    contents,
    CONTENT_TYPE.TEXT_DESCRIPTION
  )
  const buttonItem =
    getFirstContentByType(contents, CONTENT_TYPE.BUTTON_MAIN) ||
    getFirstContentByType(contents, CONTENT_TYPE.BUTTON_OTHER)

  const videoExt = parseJsonObject(videoItem?.ext)
  const imageExt = parseJsonObject(imageItem?.ext)
  const buttonExt = parseJsonObject(buttonItem?.ext)

  const splitCta =
    String(buttonItem?.content || "")
      .split("|")
      .map((item) => item.trim())
      .filter(Boolean) || []
  const splitLabel = splitCta.length >= 2 ? splitCta[0] : ""
  const splitUrl = splitCta.length >= 2 ? splitCta[1] : ""

  let graphicMode = "none"
  let graphicUrl = ""
  let graphicResource = null
  if (videoItem) {
    if (videoItem?.resourceDto) {
      graphicMode = "media"
      graphicResource = videoItem.resourceDto
    } else {
      graphicMode = "video_url"
      graphicUrl = String(
        videoExt.videoUrl ||
          videoExt.vimeoUrl ||
          videoExt.youtubeUrl ||
          videoExt.url ||
          videoExt.embedUrl ||
          videoItem?.content ||
          ""
      ).trim()
    }
  } else if (imageItem) {
    if (imageItem?.resourceDto) {
      graphicMode = "media"
      graphicResource = imageItem.resourceDto
    } else {
      graphicMode = "image_url"
      graphicUrl = String(
        imageExt.imageUrl || imageExt.url || imageExt.src || imageItem?.content || ""
      ).trim()
    }
  }

  return {
    headline: String(titleItem?.content || "").trim(),
    subHeadline: String(descriptionItem?.content || "").trim(),
    graphicMode,
    graphicUrl,
    graphicResource,
    showVideoControls: parseBoolean(
      videoExt.showControls ?? videoExt.controls,
      true
    ),
    ctaText: String(
      buttonExt.label ||
        buttonExt.text ||
        splitLabel ||
        (splitCta.length < 2 ? buttonItem?.content : "") ||
        ""
    ).trim(),
    ctaDestination: String(
      buttonExt.url || buttonExt.href || buttonExt.link || splitUrl || ""
    ).trim(),
    ctaDisplayTimeSeconds: String(
      parseNonNegativeNumber(
        buttonExt.ctaDisplayTimeSeconds ??
          buttonExt.displayTimeSeconds ??
          buttonExt.ctaDisplayTime ??
          videoExt.ctaDisplayTimeSeconds ??
          videoExt.displayTimeSeconds ??
          0
      )
    ),
    ctaOpenInNewTab: parseBoolean(
      buttonExt.newTab ?? buttonExt.targetBlank,
      false
    ),
  }
}

const mergeVideoExt = ({ existingExt, videoUrl, showVideoControls }) => {
  const parsedExt = parseJsonObject(existingExt)
  return JSON.stringify({
    ...parsedExt,
    videoUrl,
    showControls: !!showVideoControls,
  })
}

const mergeImageExt = ({ existingExt, imageUrl }) => {
  const parsedExt = parseJsonObject(existingExt)
  return JSON.stringify({
    ...parsedExt,
    imageUrl,
  })
}

const mergeButtonExt = ({
  existingExt,
  ctaDestination,
  ctaOpenInNewTab,
  ctaDisplayTimeSeconds,
}) => {
  const parsedExt = parseJsonObject(existingExt)
  return JSON.stringify({
    ...parsedExt,
    url: ctaDestination,
    newTab: !!ctaOpenInNewTab,
    ctaDisplayTimeSeconds: parseNonNegativeNumber(ctaDisplayTimeSeconds),
  })
}

const ModelDlg = (props) => {
  const initModal = {
    tag: "",
    description: "",
    showEveryTime: 0,
  }

  const { enqueueSnackbar } = useSnackbar()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      tag: "",
      description: "",
      showEveryTime: false,
    },
  })

  const { onSubmit } = props
  const [modal, setModal] = useState(initModal)
  const [modalContents, setModalContents] = useState([])
  const [campaignBuilderState, setCampaignBuilderState] = useState(
    createCampaignBuilderState()
  )
  const [currentStep, setCurrentStep] = useState(1)
  const [showGraphicOptions, setShowGraphicOptions] = useState(false)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [campaignLoading, setCampaignLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [openMediaManager, mediaManager] = MediaManager()

  const hasVideoGraphic = useMemo(() => {
    if (campaignBuilderState.graphicMode === "video_url") return true
    if (campaignBuilderState.graphicMode === "media") {
      return (
        Number(campaignBuilderState.graphicResource?.type) === MEDIA_TYPE_INDEX.video
      )
    }
    return false
  }, [campaignBuilderState])

  const handleClose = () => {
    setOpen(false)
  }

  const openDlg = async (editModal) => {
    const nextModal = editModal || initModal
    setModal(nextModal)
    setIsEditMode(Boolean(editModal))
    setModalContents([])
    setCampaignBuilderState(createCampaignBuilderState())
    setCurrentStep(1)
    setShowGraphicOptions(false)
    reset({
      tag: nextModal.tag || "",
      description: nextModal.description || "",
      showEveryTime: Number(nextModal.showEveryTime) === 1,
    })
    setOpen(true)

    if (editModal?.id) {
      setCampaignLoading(true)
      try {
        const response = await getModelContentByModalId(editModal.id)
        verifyResponse(response)
        const contents = response.data || []
        setModalContents(contents)
        setCampaignBuilderState(parseCampaignBuilderState(contents))
      } finally {
        setCampaignLoading(false)
      }
    }
  }

  const launchMediaPicker = () => {
    openMediaManager(
      (media) => {
        setCampaignBuilderState((prev) => ({
          ...prev,
          graphicMode: "media",
          graphicResource: media,
          graphicUrl: "",
        }))
      },
      [MEDIA_TYPE_INDEX.video, MEDIA_TYPE_INDEX.image]
    )
  }

  const switchGraphicMode = (mode) => {
    setCampaignBuilderState((prev) => ({
      ...prev,
      graphicMode: mode,
      graphicUrl: mode === "video_url" || mode === "image_url" ? prev.graphicUrl : "",
      graphicResource: mode === "media" ? prev.graphicResource : null,
    }))
  }

  const handleAction = async (data) => {
    const ensureSuccess = (response, failureMessage) => {
      verifyResponse(response)
      if (response?.rtnCode !== 200) {
        throw new Error(response?.msg || failureMessage)
      }
    }

    const saveCampaignContent = async (modalId) => {
      const graphicMode = campaignBuilderState.graphicMode
      const graphicUrl = String(campaignBuilderState.graphicUrl || "").trim()
      const graphicResource = campaignBuilderState.graphicResource
      const graphicResourceType = Number(graphicResource?.type || 0)

      if (graphicMode === "video_url") {
        if (!graphicUrl) {
          throw new Error("Please provide a video URL")
        }
        if (!isExternalVideoUrl(graphicUrl)) {
          throw new Error("Video URL must be a YouTube or Vimeo URL")
        }
      }
      if (graphicMode === "image_url") {
        if (!graphicUrl) {
          throw new Error("Please provide an image URL")
        }
        if (!isExternalImageUrl(graphicUrl)) {
          throw new Error("Image URL must be a valid URL")
        }
      }
      if (graphicMode === "media") {
        if (!graphicResource?.id) {
          throw new Error("Please select media from the media picker")
        }
        if (
          graphicResourceType !== MEDIA_TYPE_INDEX.video &&
          graphicResourceType !== MEDIA_TYPE_INDEX.image
        ) {
          throw new Error("Please pick a video or image from media picker")
        }
      }

      const titleItem = getFirstContentByType(modalContents, CONTENT_TYPE.TEXT_TITLE)
      const descriptionItem = getFirstContentByType(
        modalContents,
        CONTENT_TYPE.TEXT_DESCRIPTION
      )
      const videoItem = getFirstContentByType(modalContents, CONTENT_TYPE.VIDEO)
      const imageItem = getFirstContentByType(modalContents, CONTENT_TYPE.IMAGE)
      const buttonMainItem = getFirstContentByType(
        modalContents,
        CONTENT_TYPE.BUTTON_MAIN
      )
      const buttonOtherItem = getFirstContentByType(
        modalContents,
        CONTENT_TYPE.BUTTON_OTHER
      )
      const buttonItem = buttonMainItem || buttonOtherItem

      const upsertContent = async ({
        existingItem,
        type,
        content,
        ext,
        resourceId = null,
      }) => {
        const payload = {
          modalId,
          content,
          type,
          resourceId,
          ext: ext || "",
        }
        if (existingItem?.id) {
          const response = await editModalContent({
            ...payload,
            id: existingItem.id,
          })
          ensureSuccess(response, "Failed to update campaign content")
          return
        }
        const response = await addModalContent(payload)
        ensureSuccess(response, "Failed to create campaign content")
      }

      const removeContentIfExists = async (item) => {
        if (!item?.id) return
        const response = await deleteModalContent(item.id)
        ensureSuccess(response, "Failed to delete campaign content")
      }

      const trimmedHeadline = String(campaignBuilderState.headline || "").trim()
      const trimmedSubHeadline = String(campaignBuilderState.subHeadline || "").trim()
      const trimmedCtaText = String(campaignBuilderState.ctaText || "").trim()
      const trimmedCtaDestination = String(
        campaignBuilderState.ctaDestination || ""
      ).trim()

      if (trimmedHeadline) {
        await upsertContent({
          existingItem: titleItem,
          type: CONTENT_TYPE.TEXT_TITLE,
          content: trimmedHeadline,
          ext: titleItem?.ext || "",
        })
      } else {
        await removeContentIfExists(titleItem)
      }

      if (trimmedSubHeadline) {
        await upsertContent({
          existingItem: descriptionItem,
          type: CONTENT_TYPE.TEXT_DESCRIPTION,
          content: trimmedSubHeadline,
          ext: descriptionItem?.ext || "",
        })
      } else {
        await removeContentIfExists(descriptionItem)
      }

      const isVideoGraphic =
        graphicMode === "video_url" ||
        (graphicMode === "media" && graphicResourceType === MEDIA_TYPE_INDEX.video)
      const isImageGraphic =
        graphicMode === "image_url" ||
        (graphicMode === "media" && graphicResourceType === MEDIA_TYPE_INDEX.image)

      if (isVideoGraphic) {
        const videoUrl =
          graphicMode === "video_url"
            ? graphicUrl
            : String(
                graphicResource?.urlDto?.originalUrl ||
                  graphicResource?.urlDto?.hlsUrl ||
                  graphicResource?.url ||
                  ""
              ).trim()
        await upsertContent({
          existingItem: videoItem,
          type: CONTENT_TYPE.VIDEO,
          content: videoUrl,
          resourceId: graphicMode === "media" ? Number(graphicResource.id) : null,
          ext: mergeVideoExt({
            existingExt: videoItem?.ext || "",
            videoUrl,
            showVideoControls: campaignBuilderState.showVideoControls,
          }),
        })
        await removeContentIfExists(imageItem)
      } else if (isImageGraphic) {
        const imageUrl =
          graphicMode === "image_url"
            ? graphicUrl
            : String(
                graphicResource?.urlDto?.originalUrl ||
                  graphicResource?.url ||
                  ""
              ).trim()
        await upsertContent({
          existingItem: imageItem,
          type: CONTENT_TYPE.IMAGE,
          content: imageUrl,
          resourceId: graphicMode === "media" ? Number(graphicResource.id) : null,
          ext: mergeImageExt({
            existingExt: imageItem?.ext || "",
            imageUrl,
          }),
        })
        await removeContentIfExists(videoItem)
      } else {
        await removeContentIfExists(videoItem)
        await removeContentIfExists(imageItem)
      }

      const hasCtaContent = !!(trimmedCtaText || trimmedCtaDestination)
      if (hasCtaContent) {
        await upsertContent({
          existingItem: buttonItem,
          type: Number(buttonItem?.type) || CONTENT_TYPE.BUTTON_MAIN,
          content: trimmedCtaText || "Learn more",
          ext: mergeButtonExt({
            existingExt: buttonItem?.ext || "",
            ctaDestination: trimmedCtaDestination,
            ctaOpenInNewTab: campaignBuilderState.ctaOpenInNewTab,
            ctaDisplayTimeSeconds: campaignBuilderState.ctaDisplayTimeSeconds,
          }),
        })
      } else {
        await removeContentIfExists(buttonItem)
      }
    }

    try {
      setLoading(true)
      const descriptionFromCampaign = String(
        campaignBuilderState.headline ||
          campaignBuilderState.subHeadline ||
          "Dashboard campaign popup"
      ).trim()
      const description = String(data.description || "").trim() || descriptionFromCampaign
      const modalPayload = {
        ...modal,
        ...data,
        description,
        presentationType: 2,
        showEveryTime: data.showEveryTime ? 1 : 0,
      }

      const response = isEditMode
        ? await editModal(modalPayload)
        : await addModal(modalPayload)
      ensureSuccess(
        response,
        isEditMode ? "Failed to update modal" : "Failed to create modal"
      )

      let targetModalId = Number(
        isEditMode ? modal.id : response?.data?.id || response?.id || 0
      )
      if (!targetModalId) {
        const searchResponse = await searchModel()
        verifyResponse(searchResponse)
        const match = (searchResponse?.data?.list || []).find(
          (item) => String(item?.tag || "") === String(modalPayload.tag || "")
        )
        targetModalId = Number(match?.id || 0)
      }

      if (!targetModalId) {
        throw new Error("Unable to resolve modal id for campaign content")
      }

      await saveCampaignContent(targetModalId)

      enqueueSnackbar(
        isEditMode ? "Campaign modal updated" : "Campaign modal created",
        {
          variant: "success",
        }
      )

      onSubmit()
      handleClose()
    } catch (error) {
      enqueueSnackbar(
        error?.message ||
          (isEditMode
            ? "Failed to update campaign modal"
            : "Failed to create campaign modal"),
        {
          variant: "warning",
        }
      )
    } finally {
      setLoading(false)
    }
  }

  const dlg = (
    <StyledModalDialog open={open} onClose={handleClose}>
      <DialogTitle>
        <div className="dialog-title-group">
          <h2 className="dialog-title">
            {isEditMode ? "Edit Campaign Popup" : "Create Campaign Popup"}
          </h2>
          <p className="dialog-subtitle">
            Manage dashboard popup settings and campaign content in one place.
          </p>
          <div className="step-indicator">
            <span className={`step-pill ${currentStep === 1 ? "active" : ""}`}>
              1. Builder
            </span>
            <span className={`step-pill ${currentStep === 2 ? "active" : ""}`}>
              2. Campaign Setup
            </span>
          </div>
        </div>
      </DialogTitle>
      <form onSubmit={handleSubmit(handleAction)}>
        <DialogContent>
          <div className="dialog-field-group">
            {currentStep === 1 && (
              <div className="campaign-builder">
                <div className="campaign-builder-header">
                  <h3 className="campaign-builder-title">Visual Popup Builder</h3>
                  <p className="campaign-builder-subtitle">
                    Design the card content first, then continue to campaign setup.
                  </p>
                </div>

                <div
                  className="builder-media-dropzone"
                  onClick={() => setShowGraphicOptions((prev) => !prev)}
                >
                  {campaignBuilderState.graphicMode === "none" ? (
                    <div className="builder-media-empty">
                      <div className="builder-plus">+</div>
                      <p>Add Video, Image URL, or Media Picker asset</p>
                    </div>
                  ) : campaignBuilderState.graphicMode === "video_url" ? (
                    <iframe
                      src={getVideoEmbedUrl(campaignBuilderState.graphicUrl)}
                      className="builder-media-frame"
                      title="Campaign video preview"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  ) : campaignBuilderState.graphicMode === "image_url" ? (
                    <img
                      src={campaignBuilderState.graphicUrl}
                      alt="Campaign visual preview"
                      className="builder-media-image"
                    />
                  ) : (
                    <div className="builder-media-selected">
                      <MediaContentDisplay
                        resource={campaignBuilderState.graphicResource}
                      />
                    </div>
                  )}
                </div>

                {showGraphicOptions && (
                  <div className="graphic-option-grid">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => switchGraphicMode("video_url")}
                    >
                      Video URL
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => switchGraphicMode("image_url")}
                    >
                      Image URL
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={launchMediaPicker}
                    >
                      Media Picker
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      color="error"
                      onClick={() => switchGraphicMode("none")}
                    >
                      Clear Graphic
                    </Button>
                  </div>
                )}

                {(campaignBuilderState.graphicMode === "video_url" ||
                  campaignBuilderState.graphicMode === "image_url") && (
                  <TextField
                    label={
                      campaignBuilderState.graphicMode === "video_url"
                        ? "Video URL (YouTube or Vimeo)"
                        : "Image URL"
                    }
                    size="small"
                    fullWidth
                    value={campaignBuilderState.graphicUrl}
                    onChange={(event) =>
                      setCampaignBuilderState((prev) => ({
                        ...prev,
                        graphicUrl: event.target.value,
                      }))
                    }
                  />
                )}

                {campaignBuilderState.graphicMode === "media" && (
                  <div className="builder-media-actions">
                    <Button variant="outlined" size="small" onClick={launchMediaPicker}>
                      Change Media
                    </Button>
                  </div>
                )}

                <div className="builder-card">
                  <h4 className="builder-card-title">
                    {campaignBuilderState.headline || "Your popup headline"}
                  </h4>
                  <p className="builder-card-subtitle">
                    {campaignBuilderState.subHeadline ||
                      "Add a sub-headline to explain the value."}
                  </p>
                  {campaignBuilderState.ctaText && (
                    <button type="button" className="builder-card-button">
                      {campaignBuilderState.ctaText}
                    </button>
                  )}
                </div>

                <div className="campaign-builder-grid">
                  <TextField
                    label="Headline"
                    size="small"
                    value={campaignBuilderState.headline}
                    onChange={(event) =>
                      setCampaignBuilderState((prev) => ({
                        ...prev,
                        headline: event.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="Sub-headline"
                    size="small"
                    value={campaignBuilderState.subHeadline}
                    onChange={(event) =>
                      setCampaignBuilderState((prev) => ({
                        ...prev,
                        subHeadline: event.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="CTA button text"
                    size="small"
                    value={campaignBuilderState.ctaText}
                    onChange={(event) =>
                      setCampaignBuilderState((prev) => ({
                        ...prev,
                        ctaText: event.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="CTA destination URL"
                    size="small"
                    placeholder="https://..."
                    value={campaignBuilderState.ctaDestination}
                    onChange={(event) =>
                      setCampaignBuilderState((prev) => ({
                        ...prev,
                        ctaDestination: event.target.value,
                      }))
                    }
                  />
                  <TextField
                    label="CTA display time (seconds)"
                    size="small"
                    type="number"
                    value={campaignBuilderState.ctaDisplayTimeSeconds}
                    inputProps={{ min: 0, step: 1 }}
                    onChange={(event) =>
                      setCampaignBuilderState((prev) => ({
                        ...prev,
                        ctaDisplayTimeSeconds: event.target.value,
                      }))
                    }
                  />
                  <FormControlLabel
                    className="campaign-builder-toggle"
                    control={
                      <Switch
                        checked={campaignBuilderState.ctaOpenInNewTab}
                        onChange={(_, checked) =>
                          setCampaignBuilderState((prev) => ({
                            ...prev,
                            ctaOpenInNewTab: checked,
                          }))
                        }
                      />
                    }
                    label="Open CTA in new tab"
                  />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <>
                <FormControl fullWidth>
                  <TextField
                    label="Tag (query parameter value)"
                    variant="outlined"
                    size="small"
                    fullWidth
                    {...register("tag", { required: true })}
                    error={!!errors?.tag}
                    helperText={!!errors?.tag ? "Tag is required" : " "}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <TextField
                    label="URL Preview"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={buildDashboardModalUrl(watch("tag"))}
                    InputProps={{ readOnly: true }}
                    helperText="This is the URL marketing can use to trigger the popup."
                  />
                </FormControl>
                <FormControl fullWidth>
                  <TextField
                    label="Description"
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    rows={3}
                    {...register("description")}
                    helperText="Internal notes and fallback popup description."
                  />
                </FormControl>
                <FormControlLabel
                  control={
                    <Switch
                      {...{ inputProps: { "aria-label": "Show Every Time" } }}
                      checked={Boolean(watch("showEveryTime"))}
                      onChange={(event) => {
                        setValue("showEveryTime", event.target.checked)
                      }}
                    />
                  }
                  label="Show Every Time"
                />
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-cancel-btn"
            onClick={handleClose}
            variant="outlined"
            size="medium"
          >
            Cancel
          </Button>
          {currentStep === 2 && hasVideoGraphic && (
            <FormControlLabel
              className="video-controls-save-toggle"
              control={
                <Switch
                  checked={campaignBuilderState.showVideoControls}
                  onChange={(_, checked) =>
                    setCampaignBuilderState((prev) => ({
                      ...prev,
                      showVideoControls: checked,
                    }))
                  }
                />
              }
              label="Show video controls"
            />
          )}
          {currentStep === 2 && (
            <Button
              className="dialog-cancel-btn"
              onClick={() => setCurrentStep(1)}
              variant="text"
              size="medium"
            >
              Back
            </Button>
          )}
          {currentStep === 1 && (
            <Button
              className="dialog-submit-btn"
              type="button"
              variant="contained"
              size="medium"
              onClick={() => setCurrentStep(2)}
              disabled={campaignLoading}
            >
              Continue
            </Button>
          )}
          {currentStep === 2 && (
            <Button
              className="dialog-submit-btn"
              type="submit"
              variant="contained"
              size="medium"
              disabled={loading || campaignLoading}
            >
              {isEditMode ? "Save Changes" : "Create Campaign"}
            </Button>
          )}
        </DialogActions>
      </form>
      {mediaManager}
    </StyledModalDialog>
  )

  return [openDlg, dlg, loading || campaignLoading]
}

export default ModelDlg
